import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Switch,
    Space, Tag, Typography, message, Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;

const AdminSubjectTypeManager = () => {
    const [subjectTypes, setSubjectTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/subject-types');
            setSubjectTypes(res.data?.data ?? []);
        } catch {
            message.error('Không thể tải danh sách loại ngành.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openAdd = () => {
        setEditRecord(null);
        form.resetFields();
        form.setFieldsValue({ status: true });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditRecord(record);
        form.setFieldsValue({
            code: record.code,
            name: record.name,
            description: record.description,
            status: record.status,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            if (editRecord) {
                await axiosInstance.put(`/api/v1/admin/subject-types/${editRecord.id}`, values);
                message.success('Đã cập nhật loại ngành.');
            } else {
                await axiosInstance.post('/api/v1/admin/subject-types', values);
                message.success('Đã thêm loại ngành.');
            }

            setModalOpen(false);
            fetchData();
        } catch (err) {
            if (err?.errorFields) return;
            const msg = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat().join(', ')
                : 'Lưu thất bại. Vui lòng thử lại.';
            message.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/api/v1/admin/subject-types/${id}`);
            message.success('Đã xóa loại ngành.');
            fetchData();
        } catch {
            message.error('Xóa thất bại.');
        }
    };

    const columns = [
        {
            title: 'Mã',
            dataIndex: 'code',
            width: 120,
            render: (v) => <Text code>{v}</Text>,
        },
        {
            title: 'Tên loại ngành',
            dataIndex: 'name',
            ellipsis: true,
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            ellipsis: true,
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            width: 110,
            align: 'center',
            render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Hoạt động' : 'Ẩn'}</Tag>,
        },
        {
            title: '',
            width: 90,
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    <Popconfirm
                        title="Xác nhận xóa loại ngành này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout title="Quản lý loại ngành">
            <Space style={{ marginBottom: 16 }} wrap>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Thêm loại ngành
                </Button>
                {subjectTypes.length > 0 && (
                    <Text type="secondary">{subjectTypes.length} loại ngành</Text>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={subjectTypes}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 25, showTotal: (t) => `Tổng ${t} bản ghi` }}
            />

            <Modal
                title={editRecord ? 'Sửa loại ngành' : 'Thêm loại ngành'}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText={editRecord ? 'Cập nhật' : 'Thêm'}
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="code"
                        label="Mã loại ngành"
                        rules={[{ required: true, message: 'Vui lòng nhập mã.' }]}
                    >
                        <Input placeholder="VD: KHKT" />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Tên loại ngành"
                        rules={[{ required: true, message: 'Vui lòng nhập tên.' }]}
                    >
                        <Input placeholder="VD: Kỹ thuật - Công nghệ" />
                    </Form.Item>
                    <Form.Item name="description" label="Mô tả">
                        <Input.TextArea rows={3} placeholder="Mô tả ngắn (tuỳ chọn)" />
                    </Form.Item>
                    <Form.Item name="status" label="Trạng thái" valuePropName="checked">
                        <Switch checkedChildren="Hoạt động" unCheckedChildren="Ẩn" />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default AdminSubjectTypeManager;
